import { Fragment } from 'react';
import Skeleton from '../Skeleton';
import { homeStackProps } from '../../dashboard/home/homeTokens';
import useIsLandscapePhone from '../../../hooks/ui/useIsLandscapePhone';
import {
  skeletonHomeLayout, SkelCard, SkelRows, SkelGroupLabel, SkelStopsHero, SkelStopBar, Bar,
} from './homeSkeletonParts';
import { SkelWeekBand } from './homeBandSkeletons';
import { pairSpanMap } from '../../dashboard/home/pairSpans';
import { TodaySkeleton } from './AdminBillingDashboardSkeleton';

/**
 * Worker + Supervisor dashboard skeletons (KAN-971 phase C). They mirror the rebuilt field homes
 * (WorkerDashboardHome / SupervisorDashboardHome): the same stack (`homeStackProps` -> the 12-column
 * grid on web, one column under the dock), the same card order and `data-span`s, and the real HomeCard
 * shell + `cvx-home-*` layout classes filled with skeleton bars (shared parts in homeSkeletonParts.js /
 * homeBandSkeletons.js). Layout comes from the stylesheets, never from inline flex/grid here
 * (FieldRoleDashboardSkeleton.test.js pins that). Keep the ORDER below in step with those two homes
 * (CLAUDE.md skeleton parity rule). The AI briefing, 2FA and company-access rows have no placeholder.
 *
 * Spans come from `pairSpans` (KAN-1003): web places them in 12 columns, tablet in 2.
 *   supervisor  web + tablet   Today | Needs a look · Next 7 days | Recent activity   (phone: one column)
 *   worker      web + tablet   Next stop | My day · Next 7 days | Recent activity
 *               phone sideways (Next stop | My day) pair wrapper · Next 7 days · Recent activity
 *               phone upright  My day (wraps the Next stop panel) · Next 7 days · Recent activity
 *
 * Neither home has a period filter, and both bodies are padded by their page wrapper (WorkerHome /
 * SupervisorHome), so the stack carries no filter placeholder and no inset of its own.
 */

/** 6/6 pairs for web + tablet; the phone column carries no spans. */
function skeletonSpans(keys, layout) {
  const map = pairSpanMap(keys.map((key) => ({ key, kind: 'half' })), layout);
  return layout === 'phone' ? Object.fromEntries(keys.map((key) => [key, undefined])) : map;
}

/** The skeleton twin of DashboardTopChrome's stack: the same grid / column, same gap. */
function FieldSkeletonStack({ layout, testId, children }) {
  const { style, ...rest } = homeStackProps(layout);
  return (
    <div
      {...rest}
      style={{ ...style, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
      data-testid={testId}
      data-skeleton-layout={layout}
    >
      {children}
    </div>
  );
}

/** Needs a look: icon-square rows with no trailing value (the real rows carry a chevron, no text). */
function NeedsLookSkeleton({ layout, span }) {
  const web = layout === 'web';
  return (
    <SkelCard layout={layout} span={span} stretch={layout !== 'phone'} testId="needs-look-skeleton" titleWidth={104} action={60}>
      <SkelRows n={web ? 4 : 3} square trailing={false} />
    </SkelCard>
  );
}

/** Recent activity (FieldHomeActivityCard): a day label + rows per group (`groups` = rows in each). */
function FieldActivitySkeleton({ layout, span, stretch, groups = [3, 2] }) {
  return (
    <SkelCard layout={layout} span={span} stretch={stretch} testId="field-activity-skeleton" titleWidth={110} action={56}>
      <div className="cvx-home-groups">
        {groups.map((rows, i) => (
          <Fragment key={i}>
            <SkelGroupLabel />
            <SkelRows n={rows} square />
          </Fragment>
        ))}
      </div>
    </SkelCard>
  );
}

/**
 * The worker's Next stop body (WorkerNextStopCard, active state): eyebrow, big customer name, the
 * address line and the two foot buttons (Open maps + Log visit, 44px). The buttons pin to the foot of a
 * stretched card (`.cvx-home-ns__btns`); `equal` = two equal halves (a phone, and the side-by-side pair),
 * else the natural ~200 / ~160px widths.
 */
function NextStopBodySkeleton({ layout, equal = false }) {
  const web = layout === 'web';
  const btn = (basis) => ({ flex: equal ? '1 1 0' : `0 1 ${basis}px`, minWidth: 0 });
  return (
    <div className="cvx-home-ns" aria-hidden="true">
      <Bar width={116} height={10} line={16} />
      <Bar width="54%" height={web ? 24 : 21} line={web ? 34 : 29} />
      <div className="cvx-home-ns__addr">
        <Skeleton width={14} height={14} borderRadius={4} style={{ marginTop: 2 }} />
        <Bar width={176} height={11} line={20} />
      </div>
      <div className="cvx-home-ns__btns">
        <Skeleton width="100%" height={44} borderRadius={12} style={btn(200)} />
        <Skeleton width="100%" height={44} borderRadius={12} style={btn(160)} />
      </div>
    </div>
  );
}

/** The Next stop card (web: 7 wide beside My day; tablet / phone sideways: left half of the pair). */
function NextStopSkeleton({ layout, span, stretch = false, equal = false }) {
  return (
    <SkelCard layout={layout} span={span} stretch={stretch} testId="next-stop-skeleton" titleWidth={84} action={44}>
      <NextStopBodySkeleton layout={layout} equal={equal} />
    </SkelCard>
  );
}

/** My day's "Later today": label + numbered timeline rows (badge, name, address) joined by the connector line. */
function MyDayRowsSkeleton({ rows, phone }) {
  const badge = phone ? 26 : 22;
  return (
    <div className="cvx-home-md__later">
      <div className="cvx-home-md__label"><Bar width={72} height={9} line={14} /></div>
      <div className="cvx-home-md__list">
        {Array.from({ length: rows }, (_, i) => (
          <div className="cvx-home-md__row cvx-home-md__row--skel" key={i}>
            <span className="cvx-home-md__rail">
              <Skeleton width={badge} height={badge} borderRadius="50%" />
              {i < rows - 1 ? <span className="cvx-home-md__line" /> : null}
            </span>
            <span className="cvx-home-md__tx">
              <Bar width={`${50 + (i % 3) * 10}%`} height={12} line={phone ? 20 : 18} />
              <Bar width={`${38 + (i % 2) * 14}%`} height={10} line={phone ? 18 : 16} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The My day card (WorkerMyDayCard): tile + title (+ the Route link on the phone upright card), the hero
 * line and stop bar, then Later today. `phone` = the phone upright card, which also wraps the Next stop
 * panel and ends with the green "done" row.
 */
function MyDaySkeleton({ layout, span, stretch = false, phone = false, rows = 3 }) {
  return (
    <SkelCard
      layout={layout}
      span={span}
      stretch={stretch}
      titleWidth={phone ? 112 : 104}
      action={phone ? 44 : undefined}
      className={phone ? 'cvx-home-md cvx-home-md--phone' : 'cvx-home-md'}
      testId="my-day-skeleton"
    >
      <div className="cvx-home-md__prog">
        <SkelStopsHero layout={layout} />
        <SkelStopBar layout={layout} />
      </div>
      {phone ? <div className="cvx-home-ns-panel"><NextStopBodySkeleton layout={layout} equal /></div> : null}
      <MyDayRowsSkeleton rows={rows} phone={phone} />
      {phone ? (
        <div className="cvx-home-md__done" aria-hidden="true">
          <Skeleton width={26} height={26} borderRadius="50%" />
          <Bar width="64%" height={11} line={18} />
        </div>
      ) : null}
    </SkelCard>
  );
}

function SupervisorSkeleton({ layout }) {
  const web = layout === 'web';
  const sp = skeletonSpans(['today', 'needs', 'week', 'activity'], layout);
  return (
    <FieldSkeletonStack layout={layout} testId={web ? 'supervisor-dashboard-skeleton' : 'supervisor-dashboard-mobile-skeleton'}>
      <TodaySkeleton layout={layout} span={sp.today} />
      <NeedsLookSkeleton layout={layout} span={sp.needs} />
      <SkelWeekBand layout={layout} span={sp.week} stretch={layout !== 'phone'} />
      <FieldActivitySkeleton layout={layout} span={sp.activity} stretch={layout !== 'phone'} groups={web ? [3, 2] : undefined} />
    </FieldSkeletonStack>
  );
}

function WorkerSkeleton({ layout }) {
  const web = layout === 'web';
  const sideways = useIsLandscapePhone() && !web;
  // The same arrangement rule as WorkerDashboardHome: web grid · pair (tablet, phone sideways) · phone upright.
  const pair = layout === 'phone' && sideways;
  const sp = skeletonSpans(['next', 'myDay', 'week', 'activity'], layout);
  return (
    <FieldSkeletonStack layout={layout} testId={web ? 'worker-dashboard-skeleton' : 'worker-dashboard-mobile-skeleton'}>
      {layout !== 'phone' ? (
        <>
          <NextStopSkeleton layout={layout} span={sp.next} stretch equal={layout !== 'web'} />
          <MyDaySkeleton layout={layout} span={sp.myDay} stretch />
          <SkelWeekBand layout={layout} span={sp.week} stretch={layout !== 'phone'} />
          <FieldActivitySkeleton layout={layout} span={sp.activity} stretch groups={[3]} />
        </>
      ) : (
        <>
          {pair ? (
            <div className="cvx-home-pair" data-testid="worker-day-pair-skeleton">
              <NextStopSkeleton layout={layout} stretch equal />
              <MyDaySkeleton layout={layout} stretch rows={sideways ? 2 : 3} />
            </div>
          ) : (
            <MyDaySkeleton layout={layout} phone />
          )}
          <SkelWeekBand layout={layout} />
          <FieldActivitySkeleton layout={layout} />
        </>
      )}
    </FieldSkeletonStack>
  );
}
export function FieldRoleDashboardSkeleton({ isSupervisor = false, isMobile = false, isTablet = false }) {
  // Tablet floor: tablet = the SAME single-column body as the phone (the dashboard passes both flags at tablet width).
  const layout = skeletonHomeLayout(isMobile, isTablet);
  return isSupervisor ? <SupervisorSkeleton layout={layout} /> : <WorkerSkeleton layout={layout} />;
}
